import { useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import PageShell from "@/components/PageShell";
import api from "@/lib/api";
import { ArrowLeft, RotateCw, Check, X, Trophy } from "lucide-react";
import { motion } from "framer-motion";

export default function FlashcardStudy() {
  const { slug } = useParams();
  const [deck, setDeck] = useState(null);
  const [idx, setIdx] = useState(0);
  const [flipped, setFlipped] = useState(false);
  const [known, setKnown] = useState(0);
  const [reviewed, setReviewed] = useState(0);
  const [done, setDone] = useState(false);

  useEffect(() => {
    api.get(`/flashcards/${slug}`).then((r) => setDeck(r.data));
  }, [slug]);

  if (!deck) return <PageShell><div className="max-w-3xl mx-auto px-6 py-20 text-muted-foreground">Loading…</div></PageShell>;

  const card = deck.cards[idx];

  const answer = async (isKnown) => {
    try { await api.post("/progress/flashcard", { deck_id: deck.slug, card_id: card.id, known: isKnown }); } catch {}
    if (isKnown) setKnown((k) => k + 1);
    setReviewed((r) => r + 1);
    if (idx < deck.cards.length - 1) {
      setIdx(idx + 1);
      setFlipped(false);
    } else {
      setDone(true);
    }
  };

  const restart = () => { setIdx(0); setFlipped(false); setKnown(0); setReviewed(0); setDone(false); };

  return (
    <PageShell>
      <div className="max-w-3xl mx-auto px-6 py-12">
        <Link to="/flashcards" className="inline-flex items-center gap-2 text-sm text-muted-foreground hover:text-primary mb-8 transition-colors">
          <ArrowLeft className="w-4 h-4" /> All decks
        </Link>

        <div className="flex items-center justify-between mb-6">
          <div>
            <span className="text-[10px] uppercase tracking-widest font-mono text-primary">{deck.category}</span>
            <h1 className="font-serif text-3xl">{deck.title}</h1>
          </div>
          {!done && <span className="font-mono text-sm text-muted-foreground">{idx + 1} / {deck.cards.length}</span>}
        </div>

        {done ? (
          <div className="border border-border bg-card p-12 text-center">
            <Trophy className="w-12 h-12 text-primary mx-auto mb-6" />
            <h2 className="font-serif text-4xl mb-2">Deck complete</h2>
            <p className="text-muted-foreground mb-8">You knew <span className="text-primary font-mono">{known}</span> of <span className="font-mono">{reviewed}</span> cards.</p>
            <div className="flex items-center justify-center gap-4">
              <button onClick={restart} data-testid="restart-deck" className="bg-primary text-primary-foreground px-5 py-2.5 rounded-sm text-sm flex items-center gap-2 hover:-translate-y-[1px] transition-transform">
                <RotateCw className="w-4 h-4" /> Study again
              </button>
              <Link to="/flashcards" className="text-sm text-muted-foreground hover:text-primary transition-colors">Back to decks</Link>
            </div>
          </div>
        ) : (
          <>
            <div className="flip-card h-80 mb-8 cursor-pointer" onClick={() => setFlipped(!flipped)} data-testid="flashcard">
              <div className={`flip-inner ${flipped ? "flipped" : ""}`}>
                <div className="flip-face border border-border bg-card p-10 text-center">
                  <div>
                    <span className="text-[10px] uppercase tracking-widest font-mono text-primary block mb-4">Term</span>
                    <p className="font-serif text-3xl leading-tight">{card.front}</p>
                    <span className="text-xs text-muted-foreground mt-8 block">Click to reveal</span>
                  </div>
                </div>
                <div className="flip-face flip-back border border-primary bg-muted p-10 text-center overflow-y-auto">
                  <div>
                    <span className="text-[10px] uppercase tracking-widest font-mono text-primary block mb-4">Definition</span>
                    <p className="text-[15px] leading-relaxed text-foreground/90">{card.back}</p>
                  </div>
                </div>
              </div>
            </div>

            {flipped ? (
              <div className="grid grid-cols-2 gap-4">
                <button onClick={() => answer(false)} data-testid="dont-know" className="border border-border bg-card py-4 rounded-sm text-sm flex items-center justify-center gap-2 hover:border-destructive hover:text-destructive-foreground transition-colors">
                  <X className="w-4 h-4" /> Still learning
                </button>
                <button onClick={() => answer(true)} data-testid="know-it" className="bg-primary text-primary-foreground py-4 rounded-sm text-sm flex items-center justify-center gap-2 hover:-translate-y-[1px] transition-transform">
                  <Check className="w-4 h-4" /> I know this
                </button>
              </div>
            ) : (
              <motion.button
                initial={{ opacity: 0.6 }} animate={{ opacity: 1 }}
                onClick={() => setFlipped(true)}
                data-testid="reveal-btn"
                className="w-full border border-border bg-card py-4 rounded-sm text-sm text-muted-foreground hover:text-primary hover:border-primary transition-colors"
              >
                Reveal definition
              </motion.button>
            )}
          </>
        )}
      </div>
    </PageShell>
  );
}
